import { v7 as uuidv7 } from "uuid";
import * as XLSX from "xlsx";

/**
 * 创建一个异步延迟函数，接收一个毫秒数参数，返回一个 Promise 对象，在指定毫秒数后 resolve。
 * @param {number} ms - 延迟的毫秒数。
 * @returns {Promise<void>} - 无特定返回值的 Promise 对象。
 * @example sleepFn(1000).then(() => console.log('延迟 1 秒后执行'));
 */
export const sleepFn = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * 将对象序列化为字符串形式。
 * @param {Object} data 对象，键为字符串，值为任意类型。
 * @returns {string} 序列化后的字符串，格式为键值对以“&”连接。
 * @example serialize({a: 1, b: '2'}) => "a=1&b=2"
 */
export const serialize = (data: { [x: string]: any }) => {
  const list: Array<string> = [];
  Object.keys(data).forEach((ele) => {
    if (data[ele] != undefined) {
      list.push(`${ele}=${data[ele]}`);
    }
  });
  return list.join("&");
};

type FileInfo = {
  fileName: string;
  fileExtension: string;
  params: Record<"size" | string, string>;
};

/** 通过url获取文件信息 */
export const parseFileInfoFromUrl = (fileUrl: string): FileInfo => {
  const lastIndex = fileUrl.lastIndexOf("/");
  const fileNameWithExtensionAndParams = fileUrl.slice(lastIndex + 1);
  const fileNameParts = fileNameWithExtensionAndParams.split("?");
  const fileName = fileNameParts[0];
  const fileExtensionAndParams = fileNameParts[1] || "";
  const fileExtensionDotIndex = fileName.indexOf(".");
  const fileExtension = fileExtensionDotIndex !== -1 ? fileName.slice(fileExtensionDotIndex + 1).toLowerCase() : "";
  const params: Record<string, string> = {};
  if (fileExtensionAndParams) {
    const paramPairs = fileExtensionAndParams.split("&");
    paramPairs.forEach((pair) => {
      const [key, value] = pair.split("=");
      if (key && value) {
        params[key] = value;
      }
    });
  }
  return {
    fileName,
    fileExtension,
    params,
  };
};

/**
 * 格式化文件大小。
 * @param {number} size 文件大小数值。
 * @returns {string} 格式化后的文件大小字符串，如"123.45 KB"等。
 * @example formatFileSize(1536) => "1.50 KB"
 */
export const formatFileSize = (size: number): string => {
  if (!size) return "0 B";
  if (size < 1024) {
    return `${size} B`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/**
 * 获取当前时区相对于 GMT 的表示。
 * @returns {string} 返回格式为 GMT+/-HH 的字符串，表示当前时区与 GMT 的偏移。
 * @example getTimeZoneWithGMT() => "GMT+08"（假设当前时区为东八区）
 */
export const getTimeZoneWithGMT = () => {
  const offset = new Date().getTimezoneOffset();
  if (offset > 0) {
    return `GMT-${offset / 60}`;
  } else {
    return `GMT+${Math.abs(offset / 60)}`;
  }
};

/** 获取url参数 */
export const getUrlParamValue = (url: string, key: string): string | null => {
  const urlObject = new URL(url);
  const searchParams = urlObject.searchParams;
  // for (const [key, value] of searchParams) {
  //   console.log(`${key}: ${value}`);
  // }
  return searchParams.get(key) ?? null;
};

/** 获取cookie参数 */
export const getCookieValue = (key: string) => {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const parts = cookie.trim().split("=");
    if (parts[0] === key) {
      return parts[1];
    }
  }
  return null; // 如果未找到对应key，返回null
};

/** 设置cookie参数
 * @param {string} key - cookie的键。
 * @param {string} value - cookie的值。
 * @param {number} [expires] - cookie的过期时间，单位为天。
 */
export const setCookieValue = (key: string, value: string, expires?: number) => {
  let cookie = `${key}=${value}`;
  if (expires) {
    const date = new Date();
    date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
    cookie += `;expires=${date.toUTCString()}`;
  }
  document.cookie = cookie;
};

/** 最多保留两位小数并返回number类型 */
export const toFixedNumber = (num: number, digits: number): number => {
  return Number(num.toFixed(digits));
};

/**
 * 下载文件
 * @param {string} url - 文件地址
 * @param {string} fileName - 文件名
 * @returns {Promise<boolean>}
 */
export const downloadFile = (url: string, fileName: string, extension?: string): Promise<any> => {
  const newExtension = extension || parseFileInfoFromUrl(url).fileExtension;
  return new Promise((resolve, reject) => {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const objectUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = `${fileName}.${newExtension}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(objectUrl);
        document.body.removeChild(a);
        resolve(true);
      })
      .catch((error) => {
        console.error("文件下载失败:", error);
        reject(error);
      });
  });
};

/** 获取uuid */
export const getUUID = () => {
  return uuidv7().toUpperCase();
};

export const isSecondTimestamp = (timestamp: number) => {
  return /^[0-9]{10}$/.test(timestamp.toString());
};

export const isTimestamp = (timestamp: number) => {
  return /^[0-9]{13}$/.test(timestamp.toString());
};

/** 从时区A 转换为时区B的时间 */
export const convertTimeWithOffset = (dateString: any, sourceOffsetHours: number, targetOffsetHours: number) => {
  const oneHour = 60 * 60 * 1000; // 一个小时的毫秒数
  const sourceOffsetHour = sourceOffsetHours * oneHour; // 转换为分钟
  const targetOffsetHour = targetOffsetHours * oneHour; // 转换为分钟
  let modifiedDateString = dateString;
  if (isSecondTimestamp(modifiedDateString)) {
    modifiedDateString = modifiedDateString * 1000;
  }
  const localDate = new Date(modifiedDateString); // 解析输入的时间
  const __isTimestamp = isTimestamp(modifiedDateString);
  /** 如果是非时间戳，则无需处理utc偏移量 */
  const localOffsetHour = __isTimestamp ? (localDate.getTimezoneOffset() / 60) * oneHour : 0; // 获取本地时区偏移量
  const utcTimestamp = localDate.getTime() - sourceOffsetHour + localOffsetHour; // 转换为 UTC 时间
  const targetTimestamp = utcTimestamp + targetOffsetHour; // 转换为目标时区时间

  return new Date(targetTimestamp); // 返回新的 Date 实例
};

/** 只保留有值的className */
export const classNames = (classList: Record<string, any>) => {
  return Object.keys(classList)
    .filter((key) => classList[key])
    .join(" ");
};

/** 标记树节点是否为叶子节点的父节点 */
export const markLeafParents = (nodes: any[]): any[] => {
  return nodes.map((node) => {
    const children = node.children;
    if (children && children.length > 0) {
      // 判断所有子项是否都是叶子节点
      const allLeaf = children.every((child: any) => !child.children || child.children.length === 0);

      return {
        ...node,
        isLeafParent: allLeaf,
        // 递归处理下一级
        children: markLeafParents(children),
      };
    } else {
      // 无 children，不需要加标识
      return {
        ...node,
        isLeafParent: true,
      };
    }
  });
};

export const parseExcel = <T = any>(file: File) => {
  return new Promise<T>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (!e.target?.result) {
        reject(new Error("文件读取失败"));
        return;
      }
      try {
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const result = workbook.SheetNames.map((sheetName) => ({
          sheetName,
          data: XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" }),
        }));
        resolve(result as T);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
};

export const preloadImages = (srcList: string[]) => {
  return Promise.all(
    srcList.map((src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = resolve;
      });
    })
  );
};

export const preloadAudio = (srcList: string[]) => {
  return Promise.all(
    srcList.map((src) => {
      return new Promise((resolve) => {
        const audio = new Audio();
        audio.src = src;
        audio.onload = resolve;
        audio.onerror = resolve;
      });
    })
  );
};
